/*
  Frequently Bought Together — client-side logic

  - Recalcula el total del set + compare + ahorros al toggle de cada checkbox.
  - Al submit: agrega todos los seleccionados en UN request a /cart/add.js
    (Shopify soporta multi-item con {items: [...]}).
  - Despues del add: dispara 'cart:updated' para que el cart-drawer refresh+abra.
  - Guardas defensivas: no rompe si la seccion no existe o no hay checkboxes.
*/
(() => {
  const money = (cents) => {
    if (cents == null || isNaN(cents)) return '$0';
    return '$' + (cents / 100).toFixed(2);
  };

  // ============================================================
  // Countdown de urgencia por card. Cada [data-fbt-countdown][data-end]
  // recibe tick cada segundo hasta expirar. Al expirar: se oculta.
  // Usamos UN solo setInterval global (mas eficiente que uno por card).
  // ============================================================
  const activeCountdowns = new Set();

  const bootCountdowns = (scope) => {
    const els = (scope || document).querySelectorAll('[data-fbt-countdown][data-end]');
    els.forEach((el) => {
      const endStr = el.getAttribute('data-end');
      const endTime = endStr ? new Date(endStr).getTime() : NaN;
      if (isNaN(endTime)) return;
      const timeEl = el.querySelector('[data-fbt-countdown-time]');
      if (!timeEl) return;
      activeCountdowns.add({ el, endTime, timeEl });
    });
    if (!globalTickStarted && activeCountdowns.size > 0) {
      globalTickStarted = true;
      startGlobalTick();
    }
  };

  let globalTickStarted = false;
  const startGlobalTick = () => {
    const tick = () => {
      const now = Date.now();
      activeCountdowns.forEach((cd) => {
        const diff = cd.endTime - now;
        if (diff <= 0) {
          cd.el.hidden = true;
          activeCountdowns.delete(cd);
          return;
        }
        cd.el.hidden = false;
        const d = Math.floor(diff / 86400000);
        const h = Math.floor((diff / 3600000) % 24);
        const m = Math.floor((diff / 60000) % 60);
        const s = Math.floor((diff / 1000) % 60);
        const parts = [];
        if (d > 0) parts.push(d + 'd');
        parts.push(String(h).padStart(2, '0') + 'h');
        parts.push(String(m).padStart(2, '0') + 'm');
        if (d === 0) parts.push(String(s).padStart(2, '0') + 's');
        cd.timeEl.textContent = parts.join(' ');
      });
    };
    tick();
    let timer = setInterval(tick, 1000);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { if (timer) { clearInterval(timer); timer = null; } }
      else if (!timer) { tick(); timer = setInterval(tick, 1000); }
    });
  };

  // Lazy load: si complementary_products vino vacio, disparamos Section Rendering
  // para que Shopify pueble recommendations (intent complementario por default).
  const hydrateLazy = async (lazy) => {
    const url = lazy.getAttribute('data-fbt-fetch-url');
    if (!url) {
      lazy.classList.add('fbt--empty');
      lazy.classList.remove('fbt--hydrating');
      return null;
    }
    try {
      const r = await fetch(url, { credentials: 'same-origin' });
      if (!r.ok) throw new Error('fetch failed');
      const html = await r.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-fbt]');
      // Si el server no devolvio la seccion (o no hay extras), ocultamos y salimos.
      if (!fresh) {
        lazy.classList.add('fbt--empty');
        lazy.classList.remove('fbt--hydrating');
        return null;
      }
      const hasProducts = fresh.querySelectorAll('[data-fbt-check]').length > 1;
      if (!hasProducts) {
        lazy.classList.add('fbt--empty');
        lazy.classList.remove('fbt--hydrating');
        return null;
      }
      // Limpiamos flags del nodo fresco antes de reemplazar
      fresh.classList.remove('fbt--hydrating', 'fbt--empty');
      fresh.removeAttribute('data-fbt-lazy');
      lazy.replaceWith(fresh);
      return fresh;
    } catch (_) {
      lazy.classList.add('fbt--empty');
      lazy.classList.remove('fbt--hydrating');
      return null;
    }
  };

  const bootAll = async () => {
    // Hydratar los lazy primero
    const lazies = document.querySelectorAll('[data-fbt][data-fbt-lazy]');
    for (const lazy of lazies) {
      const fresh = await hydrateLazy(lazy);
      if (fresh) initFbt(fresh);
    }
    // Iniciar los que ya vinieron con productos
    document.querySelectorAll('[data-fbt]:not([data-fbt-lazy])').forEach((root) => initFbt(root));
    // Ticks de countdown para pill flash — cubre cards estaticas y hidratadas.
    bootCountdowns();
  };
  bootAll();

  function initFbt(root) {
    const checks = Array.from(root.querySelectorAll('[data-fbt-check]'));
    const totalEl = root.querySelector('[data-fbt-total]');
    const compareEl = root.querySelector('[data-fbt-compare]');
    const compareWrap = root.querySelector('[data-fbt-compare-wrap]');
    const savingsEl = root.querySelector('[data-fbt-savings]');
    const savingsWrap = root.querySelector('[data-fbt-savings-wrap]');
    const addBtn = root.querySelector('[data-fbt-add]');
    if (!checks.length || !addBtn) return;

    const recalc = () => {
      let total = 0;
      let compare = 0;
      let anyChecked = false;
      checks.forEach((c) => {
        if (!c.checked) return;
        anyChecked = true;
        const p = parseInt(c.dataset.price, 10) || 0;
        const cp = parseInt(c.dataset.compare, 10) || p;
        total += p;
        compare += Math.max(cp, p);
      });

      if (totalEl) totalEl.textContent = money(total);

      const savings = compare - total;
      if (savings > 0 && compare > total) {
        if (compareEl) compareEl.textContent = money(compare);
        if (compareWrap) compareWrap.hidden = false;
        if (savingsEl) savingsEl.textContent = money(savings);
        if (savingsWrap) savingsWrap.hidden = false;
      } else {
        if (compareWrap) compareWrap.hidden = true;
        if (savingsWrap) savingsWrap.hidden = true;
      }

      addBtn.disabled = !anyChecked;
    };

    checks.forEach((c) => c.addEventListener('change', recalc));
    recalc();

    addBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (addBtn.disabled) return;

      const items = checks
        .filter((c) => c.checked)
        .map((c) => ({
          id: Number(c.dataset.variantId),
          quantity: 1,
        }))
        .filter((it) => it.id && !isNaN(it.id));

      if (!items.length) return;

      addBtn.disabled = true;
      addBtn.classList.add('is-loading');

      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
          body: JSON.stringify({ items }),
        });
        const payload = await r.json().catch(() => null);
        if (!r.ok || (payload && payload.status && payload.status >= 400)) {
          throw new Error((payload && (payload.description || payload.message)) || 'add failed');
        }

        addBtn.classList.remove('is-loading');
        addBtn.classList.add('is-added');
        const label = addBtn.querySelector('.fbt__cta-label');
        const prev = label ? label.textContent : '';
        if (label) label.textContent = '✓ Agregado al carrito';

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'fbt' } }));
        if (window.NQLN && typeof window.NQLN.syncCartBadge === 'function') {
          window.NQLN.syncCartBadge();
        }
        if (window.NQLN && typeof window.NQLN.broadcastCartUpdate === 'function') {
          window.NQLN.broadcastCartUpdate();
        }

        setTimeout(() => {
          addBtn.classList.remove('is-added');
          if (label) label.textContent = prev;
        }, 1800);
      } catch (err) {
        addBtn.classList.remove('is-loading');
        const label = addBtn.querySelector('.fbt__cta-label');
        if (label) {
          const orig = label.textContent;
          label.textContent = 'Error al agregar';
          setTimeout(() => {
            label.textContent = orig;
          }, 1800);
        }
      } finally {
        addBtn.disabled = false;
      }
    });
  }
})();
